import {
  GridColDef,
  GridRenderCellParams,
  GridValueFormatterParams,
} from "@mui/x-data-grid-premium";
import { round, subtract } from "lodash";
import React from "react";

import ChannelStatus from "@/Components/v2/molecule/ChannelStatus/ChannelStatus";
import SatCurveLink from "@/pages/investment-levels/components/Sections/SatCurveLink";
import { MetricType } from "@/shared/types";
import { formatProductHierarchy } from "@/Utils/formatProductHierarchy";
import { formatPercentValue } from "@/Utils/formatters";

import ColumnHeader from "./components/Sections/ColumnHeader";
import MiniCurve from "./components/Sections/MiniCurve";
import { CurveType, InvestmentRating } from "./constants/maps";
import { InvestmentLevelsGridFilters } from "./store/useInvestmentLevelsStore";
import { ComparisonMode } from "./types/comparisonMode";
import { GraphDataDTO } from "./types/dto";
import { MarkData } from "./types/saturationCurve";
import { Config, NO_SYMBOL } from "@/hooks/useConfig";
import { FormatCurrencyOptions } from "@/hooks/useConfig/types";

export const getColumnDefinitions = (
  activeColumns: string[],
  options: {
    formatCurrency: (val: number, options?: FormatCurrencyOptions) => string;
    handleRowClick?: (row: any) => void;
    metricType?: MetricType;
    comparisonMode?: ComparisonMode;
    config: Config;
  }
): GridColDef[] => {
  const {
    formatCurrency,
    handleRowClick,
    metricType = MetricType.MARKETING_ROI,
    comparisonMode,
    config: { revenueLabel, showReturnInsteadOfRevenue },
  } = options;

  const revenueFormatterOptions = showReturnInsteadOfRevenue ? NO_SYMBOL : {};
  // Base columns for different comparison modes
  const columns: GridColDef<GraphDataDTO>[] = [
    {
      field: "channel",
      headerName: "Channel",
      valueGetter: ({ row }) => row.channel || "",
      renderHeader: () =>
        React.createElement(ColumnHeader, {
          header: "Channel",
        }),
      hideable: !activeColumns.includes("channel"),
    },
    ...(comparisonMode === ComparisonMode.CATEGORY || comparisonMode === ComparisonMode.PRODUCT
      ? [
          {
            field: "product",
            headerName: comparisonMode === ComparisonMode.CATEGORY ? "Category" : "Product",
            valueGetter: ({ row }: GridRenderCellParams<any, GraphDataDTO>) =>
              formatProductHierarchy(row.brand, row.category, row.product),
            renderHeader: () =>
              React.createElement(ColumnHeader, {
                header: comparisonMode === ComparisonMode.CATEGORY ? "Category" : "Product",
              }),
            hideable: !activeColumns.includes("product"),
          },
        ]
      : []),
    ...(comparisonMode === ComparisonMode.GEOGRAPHY
      ? [
          {
            field: "geography",
            headerName: "Geography",
            valueGetter: ({ row }: GridRenderCellParams<any, GraphDataDTO>) => row.geography || "",
            renderHeader: () => React.createElement(ColumnHeader, { header: "Geography" }),
            hideable: !activeColumns.includes("geography"),
          },
        ]
      : []),
    {
      field: "curve_shape",
      headerName: "Curve Shape",
      renderHeader: () =>
        React.createElement(ColumnHeader, {
          header: InvestmentLevelsGridFilters.CURVE_SHAPE,
        }),
      valueGetter: ({ row }) => row.curveValue || "",
      renderCell: ({ row }) => {
        if (row.rating === InvestmentRating.INSUFFICIENT_DATA) {
          return React.createElement("div");
        }
        return React.createElement(MiniCurve, { row });
      },
      hideable: !activeColumns.includes("curve_shape"),
      disableExport: true,
    },
    {
      field: "spend",
      headerName: "Current Spend",
      renderHeader: () =>
        React.createElement(ColumnHeader, {
          header: "Spend",
          subHeader: "Current",
        }),
      valueGetter: ({ row }) =>
        row.curveTypes[CurveType.REVENUE].markData.find((mark) => mark.key === "currentSpend")
          ?.markCoordinates?.[0] || 0,
      renderCell: ({ row }) => {
        if (row.rating === InvestmentRating.INSUFFICIENT_DATA) {
          return React.createElement("div");
        }
        const value =
          row.curveTypes[CurveType.REVENUE].markData.find((mark) => mark.key === "currentSpend")
            ?.markCoordinates?.[0] || 0;
        return formatCurrency(value);
      },
      valueFormatter: ({ value }) => formatCurrency(value || 0),
      hideable: !activeColumns.includes("spend"),
    },
    {
      field: "revenue",
      headerName: `Current ${revenueLabel}`,
      renderHeader: () =>
        React.createElement(ColumnHeader, {
          header: revenueLabel,
          subHeader: "Current",
        }),
      valueGetter: ({ row }) =>
        row.curveTypes[CurveType.REVENUE].markData.find((mark) => mark.key === "currentSpend")
          ?.markCoordinates?.[1] || 0,
      renderCell: ({ row }) => {
        if (row.rating === InvestmentRating.INSUFFICIENT_DATA) {
          return React.createElement("div");
        }
        const value =
          row.curveTypes[CurveType.REVENUE].markData.find((mark) => mark.key === "currentSpend")
            ?.markCoordinates?.[1] || 0;
        return formatCurrency(value, revenueFormatterOptions);
      },
      valueFormatter: ({ value }) => formatCurrency(value || 0, revenueFormatterOptions),
      hideable: !activeColumns.includes("revenue"),
    },
    {
      field: "weeks_in_market",
      headerName: "Weeks In Market",
      renderHeader: () =>
        React.createElement(ColumnHeader, {
          header: "Weeks",
          subHeader: "In Market",
        }),
      valueGetter: ({ row }) => row.activeWeeks || 0,
      renderCell: ({ row }) => {
        if (row.rating === InvestmentRating.INSUFFICIENT_DATA) {
          return React.createElement("div");
        }
        return row.activeWeeks || 0;
      },
      hideable: !activeColumns.includes("weeks_in_market"),
    },

    // add in roi column
    ...(metricType === MetricType.MARKETING_ROI
      ? [
          {
            field: "roi",
            headerName: "ROI",
            renderHeader: () =>
              React.createElement(ColumnHeader, {
                header: "ROI",
                subHeader: "Current",
              }),
            valueGetter: ({ row }: GridRenderCellParams<any, GraphDataDTO>) => row.roi || 0,
            renderCell: ({ row }: GridRenderCellParams<any, GraphDataDTO>) => {
              if (row.rating === InvestmentRating.INSUFFICIENT_DATA) {
                return React.createElement("div");
              }
              return row.roi || 0;
            },
            valueFormatter: ({ value }: GridValueFormatterParams) => round(Number(value) || 0, 0),
            hideable: !activeColumns.includes("roi"),
          },
        ]
      : []),

    // add in roi column
    ...(metricType === MetricType.RETURN_ON_AD_SPEND
      ? [
          {
            field: "roas",
            headerName: "ROAS",
            renderHeader: () =>
              React.createElement(ColumnHeader, {
                header: "ROAS",
                subHeader: "Current",
              }),
            valueGetter: ({ row }: GridRenderCellParams<any, GraphDataDTO>) => row.roas || 0,
            renderCell: ({ row }: GridRenderCellParams<any, GraphDataDTO>) => {
              if (row.rating === InvestmentRating.INSUFFICIENT_DATA) {
                return React.createElement("div");
              }
              return row.roas || 0;
            },
            valueFormatter: ({ value }: GridValueFormatterParams) =>
              formatPercentValue(Number(value) || 0),
            hideable: !activeColumns.includes("roas"),
          },
          {
            field: "marginal_roas",
            headerName: "Marginal ROAS",
            renderHeader: () =>
              React.createElement(ColumnHeader, {
                header: "Marginal",
                subHeader: "ROAS",
              }),
            valueGetter: ({ row }: GridRenderCellParams<any, GraphDataDTO>) =>
              row.curveTypes[CurveType.MARGINAL_ROI].markData.find(
                (mark: MarkData) => mark.key === "currentSpend"
              )?.markCoordinates?.[1] || 0,
            renderCell: ({ row }: GridRenderCellParams<any, GraphDataDTO>) => {
              if (row.rating === InvestmentRating.INSUFFICIENT_DATA) {
                return React.createElement("div");
              }
              const value =
                row.curveTypes[CurveType.MARGINAL_ROI].markData.find(
                  (mark: MarkData) => mark.key === "currentSpend"
                )?.markCoordinates?.[1] || 0;
              return round(value, 2);
            },
            hideable: !activeColumns.includes("marginal_roas"),
          },
        ]
      : []),

    // Units Column (only shown when metricType is SALES_UNITS)
    ...(metricType === MetricType.SALES_UNITS
      ? [
          {
            field: "units",
            headerName: "Units",
            renderHeader: () =>
              React.createElement(ColumnHeader, {
                header: "Units",
                subHeader: "Current",
              }),
            valueGetter: ({ row }: GridRenderCellParams<any, GraphDataDTO>) =>
              round(row.units || 0, 0),
            renderCell: ({ row }: GridRenderCellParams<any, GraphDataDTO>) => {
              if (row.rating === InvestmentRating.INSUFFICIENT_DATA) {
                return React.createElement("div");
              }
              return round(row.units || 0, 0);
            },
            valueFormatter: ({ value }: { value: unknown }) =>
              typeof value === "number" ? value.toFixed(0) : "0",
            hideable: !activeColumns.includes("units"),
          },
        ]
      : []),
    {
      field: "yield",
      headerName: "Yield",
      renderHeader: () =>
        React.createElement(ColumnHeader, {
          header: InvestmentLevelsGridFilters.YIELD,
        }),
      valueGetter: ({ row }) =>
        row.curveTypes[CurveType.YIELD].markData.find((mark) => mark.key === "currentSpend")
          ?.markCoordinates?.[1] || 0,
      renderCell: ({ row }) => {
        if (row.rating === InvestmentRating.INSUFFICIENT_DATA) {
          return React.createElement("div");
        }
        const value =
          row.curveTypes[CurveType.YIELD].markData.find((mark) => mark.key === "currentSpend")
            ?.markCoordinates?.[1] || 0;
        return formatCurrency(value, revenueFormatterOptions);
      },
      valueFormatter: ({ value }) => formatCurrency(value || 0, revenueFormatterOptions),
      hideable: !activeColumns.includes("yield"),
    },
    {
      field: "status",
      headerName: "Status",
      renderHeader: () =>
        React.createElement(ColumnHeader, {
          header: InvestmentLevelsGridFilters.STATUS,
        }),
      valueGetter: ({ row }) => row.rating || "",
      renderCell: ({ row }) => React.createElement(ChannelStatus, { status: row.rating }),
      hideable: !activeColumns.includes("status"),
    },
    {
      field: "max_efficiency",
      headerName: "Max Efficiency",
      renderHeader: () =>
        React.createElement(ColumnHeader, {
          header: InvestmentLevelsGridFilters.MAX_EFFICIENCY,
          subHeader: "Weekly spend",
        }),
      valueGetter: ({ row }) => row.maxEfficiency || 0,
      renderCell: ({ row }) => {
        if (row.rating === InvestmentRating.INSUFFICIENT_DATA) {
          return React.createElement("div");
        }
        return formatCurrency(row.maxEfficiency || 0);
      },
      valueFormatter: ({ value }) => formatCurrency(value || 0),
      hideable: !activeColumns.includes("max_efficiency"),
    },
    {
      field: "difference_to_max",
      headerName: "Difference to Max Efficiency",
      renderHeader: () =>
        React.createElement(ColumnHeader, {
          header: InvestmentLevelsGridFilters.DIFFERENCE_TO_MAX,
          subHeader: "Weekly spend",
        }),
      valueGetter: ({ row }) => {
        const currentSpend =
          row.curveTypes[CurveType.REVENUE]?.markData.find((mark) => mark.key === "currentSpend")
            ?.markCoordinates?.[0] || 0;
        return subtract(row.maxEfficiency, currentSpend || 0) || 0;
      },
      renderCell: ({ row }) => {
        if (row.rating === InvestmentRating.INSUFFICIENT_DATA) {
          return React.createElement("div");
        }
        const currentSpend =
          row.curveTypes[CurveType.REVENUE]?.markData.find((mark) => mark.key === "currentSpend")
            ?.markCoordinates?.[0] || 0;
        const diffValue = subtract(row.maxEfficiency, currentSpend || 0) || 0;
        return formatCurrency(diffValue);
      },
      valueFormatter: ({ value }) => formatCurrency(value || 0),
      hideable: !activeColumns.includes("difference_to_max"),
    },
    {
      field: "lower_threshold",
      headerName: "Lower Threshold",
      renderHeader: () =>
        React.createElement(ColumnHeader, {
          header: InvestmentLevelsGridFilters.LOWER_BOUND,
        }),
      valueGetter: ({ row }) =>
        row.curveTypes[CurveType.REVENUE]?.markData.find((mark) => mark.key === "lowerThreshold")
          ?.markCoordinates?.[0] || 0,
      renderCell: ({ row }) => {
        if (row.rating === InvestmentRating.INSUFFICIENT_DATA) {
          return React.createElement("div");
        }
        const value =
          row.curveTypes[CurveType.REVENUE]?.markData.find((mark) => mark.key === "lowerThreshold")
            ?.markCoordinates?.[0] || 0;
        return formatCurrency(value);
      },
      hideable: !activeColumns.includes("lower_threshold"),
    },
    {
      field: "upper_threshold",
      headerName: "Upper Threshold",
      renderHeader: () =>
        React.createElement(ColumnHeader, {
          header: InvestmentLevelsGridFilters.UPPER_BOUND,
        }),
      valueGetter: ({ row }) =>
        row.curveTypes[CurveType.REVENUE]?.markData.find((mark) => mark.key === "upperThreshold")
          ?.markCoordinates?.[0] || 0,
      renderCell: ({ row }) => {
        if (row.rating === InvestmentRating.INSUFFICIENT_DATA) {
          return React.createElement("div");
        }
        const value =
          row.curveTypes[CurveType.REVENUE]?.markData.find((mark) => mark.key === "upperThreshold")
            ?.markCoordinates?.[0] || 0;
        return formatCurrency(value);
      },
      valueFormatter: ({ value }) => formatCurrency(value || 0),
      hideable: !activeColumns.includes("upper_threshold"),
    },
    {
      field: "handle_click",
      headerName: "",
      hideable: false,
      disableExport: true,
      width: 80,
      minWidth: 80,
      renderCell: (params: GridRenderCellParams<GraphDataDTO>) => {
        if (params.row.rating === InvestmentRating.INSUFFICIENT_DATA) {
          return React.createElement("div");
        }
        return React.createElement(SatCurveLink, {
          onClick: () => handleRowClick && handleRowClick(params.row),
        });
      },
    },
  ];

  return columns;
};
